import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import de from "@/locales/de.json";
import en from "@/locales/en.json";
import es from "@/locales/es.json";
import fr from "@/locales/fr.json";
import it from "@/locales/it.json";
import nl from "@/locales/nl.json";
import pl from "@/locales/pl.json";
import pt from "@/locales/pt.json";

/** Le lingue dell'interfaccia: un JSON per ciascuna in src/locales, `en` e' il fallback. */
export const SUPPORTED_LOCALES = ["en", "it", "de", "es", "fr", "nl", "pl", "pt"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const LOCALE_STORAGE_KEY = "locale";

/** Nome di ogni lingua nella lingua stessa, per il selettore delle pagine pubbliche. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  it: "Italiano",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  nl: "Nederlands",
  pl: "Polski",
  pt: "Português",
};

export const LOCALE_MESSAGES: Record<Locale, Record<string, unknown>> = { en, it, de, es, fr, nl, pl, pt };

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

// La lingua arriva dal profilo utente dopo il login (setLocale) e resta in localStorage: le vecchie
// variabili d'ambiente per lingua/fallback (residuo di vue-i18n, mai configurate) non esistono piu'.
void i18next.use(initReactI18next).init({
  resources: Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [locale, { translation: LOCALE_MESSAGES[locale] }]),
  ),
  lng: readStoredLocale() ?? "en",
  fallbackLng: "en",
  // I JSON vengono da vue-i18n: {var1}, non {{var1}}.
  interpolation: { prefix: "{", suffix: "}", escapeValue: false },
  // Tre chiavi contengono "{{ }}" letterali (visualMachine.boolean2/example1/example2): senza valore
  // si restituisce il segnaposto intercettato (match[0]), non l'intera stringa, altrimenti i18next
  // lo sostituirebbe con una copia duplicata dell'intera traduzione.
  missingInterpolationHandler: (_str: string, match: RegExpMatchArray | string[]) => match[0],
  returnNull: false,
  // In questa versione di i18next (26.4.2) l'opzione initImmediate non esiste piu' nel tipo InitOptions:
  // senza backend e con le risorse gia' in memoria, init() e' comunque sincrona (t() funziona subito).
});

/** Unico punto in cui il codice nuovo cambia lingua: aggiorna i18next, <html lang> e la persistenza. */
export function setLocale(locale: Locale): void {
  if (!isLocale(locale) || locale === i18next.language) return;
  void i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* storage non disponibile */
  }
}

export const i18n = i18next;
